import apiClient from './axios';
import { ApiResponse } from '../types';

/**
 * A CAC number claimed by somebody the filing does not name.
 *
 * An RC number is not a secret — it is on invoices, letterheads and
 * contracts, and the CAC portal is searchable. Everything else at Tier 1
 * proves the company exists and that the applicant is a real person; the
 * name match is what proves they are the same person. Safe Haven does not
 * help: it verifies a director and takes the registration number as data.
 *
 * Most of these will be honest. Registers lag behind director changes, names
 * are filed differently from how people write them, and a company whose
 * listed director is another company can never match a human name at all.
 */
export interface PendingCacClaim {
  id: string;
  businessId: string;
  /** As the register writes it — which is what a reviewer sees on the
   *  certificate. */
  registrationNumber: string;
  companyName: string;
  /** The names on the filing at the moment of the claim. Stored rather than
   *  re-fetched: a lookup costs money per call, and what matters is what the
   *  register said then. */
  filedDirectors: { fullName: string; boardRole: string | null }[];
  directorId: string | null;
  /** The verified name of whoever was asking. */
  claimedByName: string | null;
  claimedByUserId: string | null;
  createdAt: string;
}

export const cacClaimsApi = {
  pending: async (): Promise<PendingCacClaim[]> => {
    const { data } = await apiClient.get<ApiResponse<PendingCacClaim[]>>(
      '/admin/cac-claims/pending',
    );
    return data.data ?? [];
  },

  review: async (
    id: string,
    decision: 'approve' | 'reject',
    note?: string,
  ): Promise<void> => {
    await apiClient.post<ApiResponse<void>>(`/admin/cac-claims/${id}/review`, {
      decision,
      note,
    });
  },
};
